<template>
  <div class="sx">
    <header class="sx-head tr-head">
      <div>
        <span class="sx-eyebrow">Transport</span>
        <h1 class="sx-title">Transports</h1>
        <p class="sx-sub">Transporteurs utilisés pour les bons d'envoi et les bons de retour, avec leur tarif par sac pour chaque zone desservie.</p>
      </div>
      <button type="button" class="btn btn-primary tr-add" @click="openModal(null)">
        <i class="ph ph-plus" aria-hidden="true"></i> Nouveau transport
      </button>
    </header>

    <!-- ── List ────────────────────────────────────────────────── -->
    <section class="sx-card" aria-labelledby="tr-list-title">
      <div class="sx-list-head">
        <h2 id="tr-list-title">Transports <span class="sx-count">{{ transports.length }}</span></h2>
        <div class="sx-search">
          <i class="ph ph-magnifying-glass" aria-hidden="true"></i>
          <input v-model="search" type="search" placeholder="Rechercher un transport…" aria-label="Rechercher un transport" />
        </div>
      </div>

      <div v-if="loading">
        <span v-for="n in 4" :key="n" class="sx-skel"></span>
      </div>
      <div v-else-if="loadError" class="sx-load-error" role="alert">
        <i class="ph ph-warning-circle" aria-hidden="true"></i>
        Impossible de charger les transports.
        <button type="button" class="btn btn-sm btn-light" @click="fetchtransports">Réessayer</button>
      </div>
      <div v-else-if="!filtered.length" class="sx-empty">
        <i class="ph ph-truck" aria-hidden="true"></i>
        <p>{{ search ? "Aucun transport ne correspond à votre recherche." : "Aucun transport pour le moment." }}</p>
      </div>
      <ul v-else class="sx-rows">
        <li v-for="item in filtered" :key="item.id" class="sx-row" :class="{ 'is-new': item.id === lastAddedId }">
          <span class="sx-avatar" aria-hidden="true">{{ initials(item.name) }}</span>
          <div class="sx-main">
            <strong>{{ item.name }}</strong>
            <small>
              <span v-if="item.code_transport" class="sx-code">{{ item.code_transport }}</span><span v-else>Sans code</span>
            </small>
          </div>
          <div class="sx-meta">
            <span class="sx-tag" :class="isActive(item) ? 'tone-success' : 'tone-danger'">
              <i class="ph" :class="isActive(item) ? 'ph-check-circle' : 'ph-x-circle'" aria-hidden="true"></i>
              {{ isActive(item) ? "Actif" : "Inactif" }}
            </span>
            <span
              class="sx-tag"
              :class="zonesOf(item).length ? 'tone-accent' : ''"
              :title="zonesOf(item).length ? zonesTitle(item) : 'Aucune zone : frais saisis à l\'expédition'"
            >
              <i class="ph ph-map-trifold" aria-hidden="true"></i>
              <template v-if="zonesOf(item).length"><b>{{ zonesOf(item).length }}</b> zone(s) · {{ priceRange(item) }} / sac</template>
              <template v-else>Sans tarif</template>
            </span>
            <span v-if="typeOf(item.type)" class="sx-tag" :class="String(item.type) === '1' ? 'tone-accent' : 'tone-info'">
              <i class="ph" :class="typeOf(item.type)?.icon" aria-hidden="true"></i>{{ typeOf(item.type)?.label }}
            </span>
            <span class="sx-tag"><i class="ph ph-paper-plane-tilt" aria-hidden="true"></i><b>{{ num(item.bons) }}</b> bon(s) d'envoi</span>
            <span class="sx-tag"><i class="ph ph-arrow-u-up-left" aria-hidden="true"></i><b>{{ num(item.bonsRN) }}</b> bon(s) retour</span>
          </div>
          <div class="sx-actions">
            <button
              type="button"
              class="sx-icon-btn"
              :aria-label="isActive(item) ? `Désactiver ${item.name}` : `Activer ${item.name}`"
              :title="isActive(item) ? 'Désactiver' : 'Activer'"
              @click="toggleActive(item)"
            >
              <i class="ph" :class="isActive(item) ? 'ph-toggle-right' : 'ph-toggle-left'" aria-hidden="true"></i>
            </button>
            <button type="button" class="sx-icon-btn" :aria-label="`Modifier ${item.name}`" title="Modifier" @click="openModal(item)">
              <i class="ph ph-pencil-simple" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="sx-icon-btn is-danger"
              :aria-label="`Supprimer ${item.name}`"
              :title="isUsed(item) ? 'Impossible : transport utilisé par des bons' : 'Supprimer'"
              :disabled="isUsed(item)"
              @click="deleteType(item.id)"
            >
              <i class="ph ph-trash" aria-hidden="true"></i>
            </button>
          </div>
        </li>
      </ul>
    </section>

    <!-- ── Add / edit modal ──────────────────────────────────────── -->
    <div id="transportModal" ref="modalEl" class="modal fade" tabindex="-1" aria-labelledby="transportModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg">
        <form class="modal-content" novalidate @submit.prevent="save">
          <div class="modal-header">
            <h2 id="transportModalLabel" class="modal-title">{{ form.id ? "Modifier le transport" : "Nouveau transport" }}</h2>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="sx-field">
              <label for="tr-name">Nom du transport <span class="sx-req" aria-hidden="true">*</span></label>
              <input
                id="tr-name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                maxlength="100"
                placeholder="Ex. CTM Messagerie"
                @input="delete errors.name"
              />
              <span v-if="errors.name" class="sx-err">{{ errors.name }}</span>
            </div>
            <div class="sx-field">
              <label for="tr-code">Code du transport</label>
              <input
                id="tr-code"
                v-model="form.code_transport"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.code_transport }"
                maxlength="50"
                placeholder="Ex. CTM"
                @input="delete errors.code_transport"
              />
              <span v-if="errors.code_transport" class="sx-err">{{ errors.code_transport }}</span>
            </div>
            <div class="sx-field">
              <label id="tr-type">Type <span class="sx-req" aria-hidden="true">*</span></label>
              <div class="sx-seg" :class="{ 'is-invalid': errors.type }" role="radiogroup" aria-labelledby="tr-type">
                <button
                  v-for="opt in typeOptions"
                  :key="opt.value"
                  type="button"
                  role="radio"
                  :aria-checked="form.type === opt.value"
                  :class="{ 'is-active': form.type === opt.value }"
                  @click="form.type = opt.value; delete errors.type"
                >
                  <i class="ph" :class="opt.icon" aria-hidden="true"></i>{{ opt.label }}
                </button>
              </div>
              <span v-if="errors.type" class="sx-err">{{ errors.type }}</span>
              <span v-else class="sx-help">Interne : votre propre flotte. Externe : transporteur partenaire.</span>
            </div>

            <!-- Zones & tariffs -->
            <div class="sx-field">
              <label id="tr-zones">Zones et tarifs par sac</label>
              <span class="sx-help tr-zones-sub">Sélectionnez les zones desservies et définissez un tarif par sac pour chaque zone.</span>

              <div v-if="form.zones.length" class="tr-zones" role="table" aria-labelledby="tr-zones">
                <div class="tr-zrow tr-zhead" role="row">
                  <span role="columnheader">Zone</span>
                  <span role="columnheader">Tarif par sac ({{ $devis }})</span>
                  <span role="columnheader"><span class="visually-hidden">Actions</span></span>
                </div>
                <div v-for="(z, i) in form.zones" :key="z.key" class="tr-zrow" role="row">
                  <div role="cell">
                    <v-select
                      v-model="z.zone_id"
                      class="tr-vselect"
                      :class="{ 'is-invalid': errors[`zones.${i}.zone_id`] }"
                      label="name"
                      :options="zoneOptions(i)"
                      :reduce="(opt) => opt.id"
                      :clearable="false"
                      placeholder="Choisir une zone..."
                      :input-id="`tr-zone-${i}`"
                      @option:selected="delete errors[`zones.${i}.zone_id`]"
                    >
                      <template #no-options>Aucune zone trouvée</template>
                    </v-select>
                    <span v-if="errors[`zones.${i}.zone_id`]" class="sx-err">{{ errors[`zones.${i}.zone_id`] }}</span>
                  </div>
                  <div role="cell">
                    <div class="input-group">
                      <input
                        v-model="z.price_per_bag"
                        type="number"
                        min="0"
                        step="0.01"
                        inputmode="decimal"
                        class="form-control"
                        :class="{ 'is-invalid': errors[`zones.${i}.price_per_bag`] }"
                        placeholder="Saisir le tarif"
                        :aria-label="`Tarif par sac de la ligne ${i + 1}`"
                        @input="delete errors[`zones.${i}.price_per_bag`]"
                      />
                      <span class="input-group-text">{{ $devis }}</span>
                    </div>
                    <span v-if="errors[`zones.${i}.price_per_bag`]" class="sx-err">{{ errors[`zones.${i}.price_per_bag`] }}</span>
                  </div>
                  <div role="cell">
                    <button type="button" class="tr-zdel" :aria-label="`Retirer la ligne ${i + 1}`" title="Retirer" @click="removeZone(i)">
                      <i class="ph ph-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </div>
              </div>

              <button type="button" class="btn tr-zadd" :disabled="!canAddZone" @click="addZone">
                <i class="ph ph-plus" aria-hidden="true"></i> Ajouter une zone
              </button>
              <span v-if="zonesError" class="sx-err">{{ zonesError }}</span>
              <span v-else-if="!form.zones.length" class="sx-help">Sans zone, les frais de transport sont saisis à l'expédition.</span>
              <span v-if="form.legacyPrice !== null" class="sx-help tr-legacy">
                <i class="ph ph-info" aria-hidden="true"></i>
                Ancien tarif unique : {{ money(form.legacyPrice) }} / sac. Il sera remplacé par les tarifs par zone à l'enregistrement.
              </span>
              <span v-if="form.id" class="sx-help">S'applique aux prochaines expéditions ; les bons déjà expédiés gardent leurs frais.</span>
            </div>

            <div class="sx-field mb-0">
              <label for="tr-active">Statut</label>
              <div class="form-check form-switch tr-switch">
                <input id="tr-active" v-model="form.active" class="form-check-input" type="checkbox" role="switch" />
                <label class="form-check-label" for="tr-active">{{ form.active ? "Actif" : "Inactif" }}</label>
              </div>
              <span class="sx-help">Un transport inactif n'est plus proposé à l'expédition des bons.</span>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Annuler</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm" role="status"></span>
              {{ form.id ? "Enregistrer" : "Ajouter le transport" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import api from "@/utils/axios";
import { currency } from "@/utils/currency";
import { useAppToast } from "@/composables/useAppToast";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";

interface ZonePrice {
  zone_id: number;
  zone_name: string;
  price_per_bag: number;
}

interface Transport {
  id: number;
  name: string;
  code_transport: string | null;
  type: number | string | null;
  price_per_bag: number | string | null;
  active: number | boolean | null;
  zones?: ZonePrice[];
  bons?: number;
  bonsRN?: number;
}

interface Zone {
  id: number;
  name: string;
}

interface ZoneLine {
  key: number;
  zone_id: number | null;
  price_per_bag: string | number;
}

const typeOptions = [
  { value: "1", label: "Interne", icon: "ph-house-line" },
  { value: "2", label: "Externe", icon: "ph-handshake" },
];

const norm = (s: string) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export default defineComponent({
  name: "TransportSettings",
  setup() {
    const { showToast } = useAppToast();
    const transports = ref<Transport[]>([]);
    const zones = ref<Zone[]>([]);
    const loading = ref(true);
    const loadError = ref(false);
    const search = ref("");
    const lastAddedId = ref<number | null>(null);

    const num = (v: unknown) => Number(v) || 0;
    const isUsed = (t: Transport) => num(t.bons) > 0 || num(t.bonsRN) > 0;
    const isActive = (t: Transport) => Number(t.active) === 1;
    const money = (v: unknown) =>
      Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ` ${currency()}`;
    const typeOf = (v: unknown) => typeOptions.find((o) => o.value === String(v ?? ""));
    const zonesOf = (t: Transport) => t.zones || [];
    const zonesTitle = (t: Transport) => zonesOf(t).map((z) => `${z.zone_name} : ${money(z.price_per_bag)}`).join("\n");
    const priceRange = (t: Transport) => {
      const prices = zonesOf(t).map((z) => Number(z.price_per_bag));
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return min === max ? money(min) : `${money(min).replace(` ${currency()}`, "")} – ${money(max)}`;
    };
    const initials = (name: string) =>
      (name || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p[0] || "")
        .join("")
        .toUpperCase();

    async function fetchtransports() {
      loading.value = true;
      loadError.value = false;
      try {
        const response = await api.get("/api/auth/AllTransport");
        transports.value = response.data || [];
      } catch (error) {
        console.error("Error fetching transports:", error);
        loadError.value = true;
      } finally {
        loading.value = false;
      }
    }
    async function fetchZones() {
      try {
        const response = await api.get("/api/auth/ListZone");
        zones.value = (response.data || [])
          .map((z: Zone) => ({ id: z.id, name: String(z.name || "").trim() }))
          .sort((a: Zone, b: Zone) => a.name.localeCompare(b.name, "fr"));
      } catch (error) {
        console.error("Error fetching zones:", error);
      }
    }
    onMounted(() => {
      fetchtransports();
      fetchZones();
    });

    const filtered = computed(() => {
      const q = norm(search.value.trim());
      return q
        ? transports.value.filter((t) => norm(t.name).includes(q) || norm(t.code_transport || "").includes(q))
        : transports.value;
    });

    const nameTaken = (name: string, exceptId: number | null = null) =>
      transports.value.some((t) => t.id !== exceptId && norm(t.name) === norm(name));

    // ── Add / edit (same modal) ──
    let lineKey = 0;
    const form = reactive<{
      id: number | null;
      name: string;
      code_transport: string;
      type: string;
      active: boolean;
      zones: ZoneLine[];
      legacyPrice: number | null;
    }>({ id: null, name: "", code_transport: "", type: "", active: true, zones: [], legacyPrice: null });
    const errors = reactive<Record<string, string>>({});
    const zonesError = ref("");
    const saving = ref(false);
    const modalEl = ref<HTMLElement | null>(null);
    let modalInstance: Modal | null = null;

    const clearErrors = () => {
      Object.keys(errors).forEach((k) => delete errors[k]);
      zonesError.value = "";
    };

    // a zone can be chosen only once per transport
    const zoneOptions = (index: number) => {
      const taken = new Set(form.zones.filter((_, i) => i !== index).map((z) => z.zone_id));
      return zones.value.filter((z) => !taken.has(z.id));
    };
    const canAddZone = computed(() => form.zones.length < zones.value.length);
    const addZone = () => {
      form.zones.push({ key: ++lineKey, zone_id: null, price_per_bag: "" });
      zonesError.value = "";
    };
    const removeZone = (index: number) => {
      form.zones.splice(index, 1);
      // row errors are indexed: drop them all rather than shifting them
      Object.keys(errors).filter((k) => k.startsWith("zones.")).forEach((k) => delete errors[k]);
      zonesError.value = "";
    };

    const openModal = (item: Transport | null) => {
      clearErrors();
      form.id = item?.id ?? null;
      form.name = item?.name ?? "";
      form.code_transport = item?.code_transport || "";
      form.type = item ? typeOf(item.type)?.value || "" : "";
      form.active = item ? isActive(item) : true;
      form.zones = zonesOf(item || ({} as Transport)).map((z) => ({ key: ++lineKey, zone_id: z.zone_id, price_per_bag: z.price_per_bag }));
      form.legacyPrice =
        item && item.price_per_bag !== null && item.price_per_bag !== "" && item.price_per_bag !== undefined ? Number(item.price_per_bag) : null;
      if (!form.zones.length) addZone();
      if (!modalInstance && modalEl.value) modalInstance = new Modal(modalEl.value);
      modalInstance?.show();
    };

    const save = async () => {
      clearErrors();
      const name = form.name.trim();
      if (!name) errors.name = "Le nom du transport est obligatoire.";
      else if (nameTaken(name, form.id)) errors.name = "Ce transport existe déjà.";
      if (!form.type) errors.type = "Choisissez le type de transport.";

      // fully empty lines are ignored; half-filled ones must be completed
      const lines = form.zones.filter((z) => z.zone_id !== null || String(z.price_per_bag ?? "").trim() !== "");
      form.zones.forEach((z, i) => {
        if (!lines.includes(z)) return;
        const price = String(z.price_per_bag ?? "").trim().replace(",", ".");
        if (z.zone_id === null) errors[`zones.${i}.zone_id`] = "Choisissez une zone.";
        if (price === "") errors[`zones.${i}.price_per_bag`] = "Saisissez le tarif.";
        else if (isNaN(Number(price)) || Number(price) < 0) errors[`zones.${i}.price_per_bag`] = "Tarif invalide.";
      });
      if (Object.keys(errors).length || saving.value) return;

      saving.value = true;
      const payload = {
        name,
        code_transport: form.code_transport.trim(),
        type: form.type,
        active: form.active ? 1 : 0,
        zones: lines.map((z) => ({ zone_id: z.zone_id, price_per_bag: String(z.price_per_bag).trim().replace(",", ".") })),
      };
      try {
        const editing = form.id !== null;
        const res = editing
          ? await api.post(`/api/auth/trans_edit/${form.id}`, payload)
          : await api.post("/api/auth/NewTransport", payload);
        if (res.data?.success) {
          modalInstance?.hide();
          const before = new Set(transports.value.map((t) => t.id));
          await fetchtransports();
          if (!editing) lastAddedId.value = transports.value.find((t) => !before.has(t.id))?.id ?? null;
          showToast(editing ? "Transport modifié." : "Transport ajouté.", "success");
        } else {
          showToast(res.data?.message ?? "L'enregistrement a échoué.", "error");
        }
      } catch (error: any) {
        const errs = error?.response?.data?.errors || {};
        // server indexes refer to the sent lines: map them back to the displayed rows
        Object.keys(errs).forEach((k) => {
          const m = k.match(/^zones\.(\d+)\.(.+)$/);
          if (m) {
            const row = form.zones.indexOf(lines[Number(m[1])]);
            if (row >= 0) errors[`zones.${row}.${m[2]}`] = errs[k][0];
            else zonesError.value = errs[k][0];
          } else if (k === "zones") {
            zonesError.value = errs[k][0];
          } else {
            errors[k] = errs[k][0];
          }
        });
        showToast(error?.response?.data?.message || "Une erreur est survenue lors de l'enregistrement.", "error");
      } finally {
        saving.value = false;
      }
    };
    onBeforeUnmount(() => modalInstance?.dispose());

    // ── Delete (the server refuses transports used by notes) ──
    function deleteType(id: number) {
      const target = transports.value.find((t) => t.id === id);
      Swal.fire({
        title: "Supprimer ce transport ?",
        text: target ? `« ${target.name} » sera définitivement supprimé.` : "",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#E44A4A",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        reverseButtons: true,
      }).then(async (result: any) => {
        if (!result.isConfirmed) return;
        try {
          const res = await api.post(`/api/auth/removeTrans/${id}`);
          if (res.data?.success) {
            showToast("Transport supprimé.", "success");
            fetchtransports();
          } else {
            showToast(res.data?.message ?? "Vous ne pouvez pas supprimer ce transport.", "error");
          }
        } catch (error: any) {
          showToast(error?.response?.data?.message || "Erreur lors de la suppression du transport.", "error");
        }
      });
    }

    // ── Active / inactive ──
    async function toggleActive(item: Transport) {
      try {
        const res = await api.post(`/api/auth/trans_toggle/${item.id}`);
        if (res.data?.success) {
          item.active = res.data.active;
          showToast(isActive(item) ? "Transport activé." : "Transport désactivé.", "success");
        }
      } catch (error: any) {
        showToast(error?.response?.data?.message || "Erreur lors de la mise à jour.", "error");
      }
    }

    return {
      transports,
      loading,
      loadError,
      search,
      filtered,
      lastAddedId,
      typeOptions,
      num,
      isUsed,
      isActive,
      money,
      typeOf,
      zonesOf,
      zonesTitle,
      priceRange,
      initials,
      fetchtransports,
      toggleActive,
      deleteType,
      form,
      errors,
      zonesError,
      saving,
      modalEl,
      zoneOptions,
      canAddZone,
      addZone,
      removeZone,
      openModal,
      save,
    };
  },
});
</script>

<style scoped>
.tr-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.tr-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.sx-tag.tone-success {
  background: var(--ui-success-soft);
  color: var(--ui-success);
}
.sx-tag.tone-danger {
  background: var(--ui-danger-soft);
  color: var(--ui-danger);
}
.tr-switch {
  padding-top: 6px;
}

/* Zones & tariffs */
.tr-zones-sub {
  margin: -2px 0 10px;
}
.tr-zones {
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
}
.tr-zrow {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) 64px;
  align-items: start;
}
.tr-zrow > * {
  padding: 8px 12px;
}
.tr-zrow > * + * {
  border-left: 1px solid var(--ui-border);
}
.tr-zrow + .tr-zrow {
  border-top: 1px solid var(--ui-border);
}
.tr-zhead {
  background: var(--ui-surface-sunken);
  font-size: var(--ui-fs-sm);
  font-weight: var(--ui-fw-heavy);
}
.tr-zhead > * {
  padding: 10px 12px;
}
.tr-zhead {
  border-radius: var(--ui-radius-md) var(--ui-radius-md) 0 0;
}
.tr-vselect {
  --vs-border-color: var(--ui-border);
  --vs-border-radius: var(--ui-radius-sm);
}
.tr-vselect.is-invalid {
  --vs-border-color: var(--ui-danger);
}
.tr-vselect :deep(.vs__dropdown-toggle) {
  min-height: 38px;
}
.tr-vselect :deep(.vs__dropdown-menu) {
  z-index: 1060;
}
.tr-zrow .input-group-text {
  color: var(--ui-text-muted);
  font-size: var(--ui-fs-sm);
}
.tr-zdel {
  display: grid;
  place-items: center;
  width: 40px;
  height: 38px;
  margin: 0 auto;
  border: 1px solid var(--ui-danger);
  border-radius: var(--ui-radius-sm);
  background: transparent;
  color: var(--ui-danger);
  font-size: 18px;
  transition: background var(--ui-dur-fast) var(--ui-ease);
}
.tr-zdel:hover,
.tr-zdel:focus-visible {
  background: var(--ui-danger-soft);
}
.tr-zadd {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  border: 1px solid var(--ui-accent);
  color: var(--ui-accent-text);
  font-weight: var(--ui-fw-heavy);
}
.tr-zadd:hover:not(:disabled) {
  background: var(--ui-accent-soft);
}
.tr-legacy {
  color: var(--ui-warning, var(--ui-text-muted));
}

@media (max-width: 575.98px) {
  .tr-zhead {
    display: none;
  }
  .tr-zrow {
    grid-template-columns: minmax(0, 1fr) 56px;
  }
  .tr-zrow > :first-child {
    grid-column: 1 / -1;
    border-bottom: 1px solid var(--ui-border);
  }
  .tr-zrow > :nth-child(2) {
    border-left: 0;
  }
}
</style>
